import { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  createSiteUpdate,
  deleteSiteUpdate,
  fetchSiteUpdate,
  fetchSiteUpdates,
  updateSiteUpdate,
} from '../../api/siteUpdate';
import { useAuthStore } from '../../store/authStore';
import { SiteUpdate } from '../../types/siteUpdate';
import { notifyError } from '../../utils/notify';

function formatDate(value?: string | null) {
  if (!value) return '-';
  return value.slice(0, 10);
}

type Mode = 'list' | 'detail' | 'create' | 'edit';

function LandingUpdatesSection() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [mode, setMode] = useState<Mode>('list');
  const [rows, setRows] = useState<SiteUpdate[]>([]);
  const [detail, setDetail] = useState<SiteUpdate | null>(null);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [releasedAt, setReleasedAt] = useState(new Date().toISOString().slice(0, 10));
  const [editId, setEditId] = useState<number | null>(null);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchSiteUpdates({ page: 0, size: 30 });
      setRows(result.content ?? []);
      setCanManage(Boolean(result.canManage) && isAuthenticated);
    } catch {
      setRows([]);
      setCanManage(false);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const openDetail = async (updateId: number) => {
    try {
      const data = await fetchSiteUpdate(updateId);
      setDetail(data);
      setMode('detail');
    } catch {
      notifyError('업데이트를 불러오지 못했습니다.');
    }
  };

  const openCreate = () => {
    setTitle('');
    setContent('');
    setReleasedAt(new Date().toISOString().slice(0, 10));
    setEditId(null);
    setMode('create');
  };

  const openEdit = (row: SiteUpdate) => {
    setEditId(row.updateId);
    setTitle(row.title || '');
    setContent(row.content || '');
    setReleasedAt(row.releasedAt?.slice(0, 10) || new Date().toISOString().slice(0, 10));
    setMode('edit');
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim() || !content.trim()) {
      notifyError('제목과 내용을 입력해 주세요.');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        title: title.trim(),
        content: content.trim(),
        releasedAt: releasedAt || null,
      };
      if (mode === 'edit' && editId != null) {
        const updated = await updateSiteUpdate(editId, payload);
        setDetail(updated);
        setMode('detail');
      } else {
        const created = await createSiteUpdate(payload);
        setDetail(created);
        setMode('detail');
      }
      await loadList();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '저장에 실패했습니다.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: SiteUpdate) => {
    if (!window.confirm(`"${row.title}" 업데이트를 삭제할까요?`)) return;
    try {
      await deleteSiteUpdate(row.updateId);
      setDetail(null);
      setMode('list');
      await loadList();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '삭제에 실패했습니다.'
      );
    }
  };

  return (
    <section className="ll-landing__updates" id="updates" aria-label="업데이트">
      <div className="ll-landing__updates-inner">
        <div className="ll-landing__updates-head">
          <div>
            <p className="ll-landing__updates-eyebrow">Updates</p>
            <h2>업데이트 내역</h2>
            <p>Linelog에 새로 반영된 기능을 확인하세요.</p>
          </div>
          <div className="ll-landing__updates-actions">
            {mode !== 'list' ? (
              <button type="button" className="ll-landing__updates-btn is-ghost" onClick={() => setMode('list')}>
                목록
              </button>
            ) : null}
            {canManage && mode === 'list' ? (
              <button type="button" className="ll-landing__updates-btn is-primary" onClick={openCreate}>
                등록
              </button>
            ) : null}
          </div>
        </div>

        {mode === 'list' ? (
          loading ? (
            <p className="ll-landing__updates-empty">불러오는 중...</p>
          ) : rows.length === 0 ? (
            <p className="ll-landing__updates-empty">등록된 업데이트가 없습니다.</p>
          ) : (
            <ul className="ll-landing__updates-list">
              {rows.map((row) => (
                <li key={row.updateId}>
                  <button type="button" onClick={() => void openDetail(row.updateId)}>
                    <time dateTime={row.releasedAt || undefined}>{formatDate(row.releasedAt)}</time>
                    <strong>{row.title}</strong>
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : null}

        {mode === 'detail' && detail ? (
          <article className="ll-landing__updates-detail">
            <time dateTime={detail.releasedAt || undefined}>{formatDate(detail.releasedAt)}</time>
            <h3>{detail.title}</h3>
            <p className="ll-landing__updates-meta">{detail.authorName || 'Linelog'}</p>
            <div className="ll-landing__updates-body">{detail.content}</div>
            {canManage ? (
              <div className="ll-landing__updates-actions" style={{ marginTop: 20 }}>
                <button type="button" className="ll-landing__updates-btn is-ghost" onClick={() => openEdit(detail)}>
                  수정
                </button>
                <button
                  type="button"
                  className="ll-landing__updates-btn is-danger"
                  onClick={() => void handleDelete(detail)}
                >
                  삭제
                </button>
              </div>
            ) : null}
          </article>
        ) : null}

        {mode === 'create' || mode === 'edit' ? (
          <form className="ll-landing__updates-form" onSubmit={(e) => void handleSubmit(e)}>
            <label>
              <span>일자</span>
              <input type="date" value={releasedAt} onChange={(e) => setReleasedAt(e.target.value)} required />
            </label>
            <label>
              <span>제목</span>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                placeholder="업데이트 제목"
                required
              />
            </label>
            <label>
              <span>내용</span>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={10}
                placeholder="변경 내용을 적어 주세요."
                required
              />
            </label>
            <div className="ll-landing__updates-actions">
              <button
                type="button"
                className="ll-landing__updates-btn is-ghost"
                onClick={() => (mode === 'edit' && detail ? setMode('detail') : setMode('list'))}
              >
                취소
              </button>
              <button type="submit" className="ll-landing__updates-btn is-primary" disabled={saving}>
                {saving ? '저장 중...' : mode === 'edit' ? '수정 저장' : '등록'}
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </section>
  );
}

export default LandingUpdatesSection;
